import { useContext, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from "react";
import { NodeViewWrapper } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { AlignCenter, AlignLeft, AlignRight, PictureInPicture2, Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";

import { ImageActionsContext } from "./image-actions";
import { useEditorReadOnly } from "./fields-context";
import { ControlButton, ResizeHandles } from "./resize-handles";
import type { Corner } from "./resize-handles";

const MIN_WIDTH = 24;

const controlGuard = {
	onMouseDown: (event: ReactMouseEvent<HTMLButtonElement>) => event.preventDefault(),
};

export function ImageView(props: NodeViewProps) {
	const actions = useContext(ImageActionsContext);
	const readOnly = useEditorReadOnly();
	const frameRef = useRef<HTMLElement>(null);
	const imgRef = useRef<HTMLImageElement>(null);
	const dragRef = useRef<{
		pointerId: number;
		startX: number;
		startWidth: number;
		corner: Corner;
	} | null>(null);
	const [previewWidth, setPreviewWidth] = useState<number | null>(null);

	const { file, width, height, align } = props.node.attrs as {
		file: string;
		width: number;
		height: number;
		align: "center" | "right" | null;
	};
	const aspect = height / width;
	const shownWidth = previewWidth ?? width;

	const maxWidth = () => {
		const flow = frameRef.current?.parentElement;

		return flow === null || flow === undefined ? Number.POSITIVE_INFINITY : flow.clientWidth;
	};

	const widthFrom = (corner: Corner, startWidth: number, dx: number) => {
		const delta = corner === "nw" || corner === "sw" ? -dx : dx;

		return Math.round(Math.min(maxWidth(), Math.max(MIN_WIDTH, startWidth + delta)));
	};

	const startResize = (corner: Corner) => (event: ReactPointerEvent<HTMLSpanElement>) => {
		if (event.button !== 0) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		event.currentTarget.setPointerCapture(event.pointerId);
		dragRef.current = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startWidth: width,
			corner,
		};
	};

	const moveResize = (event: ReactPointerEvent<HTMLSpanElement>) => {
		const drag = dragRef.current;

		if (drag === null || event.pointerId !== drag.pointerId) {
			return;
		}

		setPreviewWidth(widthFrom(drag.corner, drag.startWidth, event.clientX - drag.startX));
	};

	const endResize = (event: ReactPointerEvent<HTMLSpanElement>) => {
		const drag = dragRef.current;

		if (drag === null || event.pointerId !== drag.pointerId) {
			return;
		}

		const next = widthFrom(drag.corner, drag.startWidth, event.clientX - drag.startX);

		dragRef.current = null;
		setPreviewWidth(null);
		props.updateAttributes({ width: next, height: Math.max(1, Math.round(next * aspect)) });
	};

	const setAlign = (value: "center" | "right" | null) => {
		props.updateAttributes({ align: value });
	};

	const requestFloat = () => {
		const pos = props.getPos();

		if (actions === null || pos === undefined || imgRef.current === null) {
			return;
		}

		actions.toFloat({ pos, file, width, height, dom: imgRef.current });
	};

	return (
		<NodeViewWrapper
			as="figure"
			ref={frameRef}
			data-drag-handle={readOnly ? undefined : ""}
			className={cn("doc-image", align)}
		>
			<span className="relative inline-flex" contentEditable={false}>
				<img
					ref={imgRef}
					src={`/terms/image/${file}`}
					alt=""
					draggable={false}
					className={cn("block select-none", props.selected && !readOnly && "ring-2 ring-primary")}
					style={{ width: shownWidth, height: Math.max(1, Math.round(shownWidth * aspect)) }}
				/>

				{props.selected && !readOnly && (
					<ResizeHandles
						onCornerPointerDown={startResize}
						onPointerMove={moveResize}
						onPointerUp={endResize}
					/>
				)}

				{props.selected && !readOnly && (
					<span
						data-slot="image-actions"
						className="absolute -top-10 left-0 z-20 flex items-center gap-0.5 surface-panel p-1 shadow-card"
					>
						<ControlButton
							label="Alinhar à esquerda"
							guard={controlGuard}
							active={align === null}
							onClick={() => setAlign(null)}
						>
							<AlignLeft />
						</ControlButton>

						<ControlButton
							label="Centralizar"
							guard={controlGuard}
							active={align === "center"}
							onClick={() => setAlign("center")}
						>
							<AlignCenter />
						</ControlButton>

						<ControlButton
							label="Alinhar à direita"
							guard={controlGuard}
							active={align === "right"}
							onClick={() => setAlign("right")}
						>
							<AlignRight />
						</ControlButton>

						{actions !== null && (
							<ControlButton label="Flutuar na página" guard={controlGuard} onClick={requestFloat}>
								<PictureInPicture2 />
							</ControlButton>
						)}

						<ControlButton
							label="Remover imagem"
							guard={controlGuard}
							onClick={() => props.deleteNode()}
						>
							<Trash2 />
						</ControlButton>
					</span>
				)}
			</span>
		</NodeViewWrapper>
	);
}
